import React, { useState, useCallback, useMemo } from "react";
import { ChevronRight } from "lucide-react";

// Memoize the static SVG background to prevent recreation on every render
const STATIC_BACKGROUND = {
  backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
};

// Color schemes for different tiles
const COLOR_SCHEMES = [
  // Amber (original)
  {
    bg: "bg-amber-50/40",
    border: "border-amber-100/60",
    shadow: "shadow-amber-200/20",
    shadowHover: "shadow-amber-300/30",
    gradientFrom: "from-amber-50/50",
    gradientTo: "to-amber-100/30",
    backdrop: "bg-amber-50/25",
    iconBg: "bg-amber-100/50",
    iconBorder: "border-amber-200/60",
    text: "text-amber-900",
    textMuted: "text-amber-800/80",
    chevron: "text-amber-700/70",
    chevronHover: "text-amber-800",
    hoverBorder: "border-amber-200/50"
  },
  // Blue
  {
    bg: "bg-blue-50/40",
    border: "border-blue-100/60",
    shadow: "shadow-blue-200/20",
    shadowHover: "shadow-blue-300/30",
    gradientFrom: "from-blue-50/50",
    gradientTo: "to-blue-100/30",
    backdrop: "bg-blue-50/25",
    iconBg: "bg-blue-100/50",
    iconBorder: "border-blue-200/60",
    text: "text-blue-900",
    textMuted: "text-blue-800/80",
    chevron: "text-blue-700/70",
    chevronHover: "text-blue-800",
    hoverBorder: "border-blue-200/50"
  },
  // Green
  {
    bg: "bg-green-50/40",
    border: "border-green-100/60",
    shadow: "shadow-green-200/20",
    shadowHover: "shadow-green-300/30",
    gradientFrom: "from-green-50/50",
    gradientTo: "to-green-100/30",
    backdrop: "bg-green-50/25",
    iconBg: "bg-green-100/50",
    iconBorder: "border-green-200/60",
    text: "text-green-900",
    textMuted: "text-green-800/80",
    chevron: "text-green-700/70",
    chevronHover: "text-green-800",
    hoverBorder: "border-green-200/50"
  },
  // Violet
  {
    bg: "bg-violet-50/40",
    border: "border-violet-100/60",
    shadow: "shadow-violet-200/20",
    shadowHover: "shadow-violet-300/30",
    gradientFrom: "from-violet-50/50",
    gradientTo: "to-violet-100/30",
    backdrop: "bg-violet-50/25",
    iconBg: "bg-violet-100/50",
    iconBorder: "border-violet-200/60",
    text: "text-violet-900",
    textMuted: "text-violet-800/80",
    chevron: "text-violet-700/70",
    chevronHover: "text-violet-800",
    hoverBorder: "border-violet-200/50"
  },
  // Rose
  {
    bg: "bg-rose-50/40",
    border: "border-rose-100/60",
    shadow: "shadow-rose-200/20",
    shadowHover: "shadow-rose-300/30",
    gradientFrom: "from-rose-50/50",
    gradientTo: "to-rose-100/30",
    backdrop: "bg-rose-50/25",
    iconBg: "bg-rose-100/50",
    iconBorder: "border-rose-200/60",
    text: "text-rose-900",
    textMuted: "text-rose-800/80",
    chevron: "text-rose-700/70",
    chevronHover: "text-rose-800",
    hoverBorder: "border-rose-200/50"
  },
  // Cyan
  {
    bg: "bg-cyan-50/40",
    border: "border-cyan-100/60",
    shadow: "shadow-cyan-200/20",
    shadowHover: "shadow-cyan-300/30",
    gradientFrom: "from-cyan-50/50",
    gradientTo: "to-cyan-100/30",
    backdrop: "bg-cyan-50/25",
    iconBg: "bg-cyan-100/50",
    iconBorder: "border-cyan-200/60",
    text: "text-cyan-900",
    textMuted: "text-cyan-800/80",
    chevron: "text-cyan-700/70",
    chevronHover: "text-cyan-800",
    hoverBorder: "border-cyan-200/50"
  }
];

// Helper function to get color scheme based on title or index
const getColorScheme = (title, colorIndex) => {
  if (colorIndex !== undefined) {
    return COLOR_SCHEMES[colorIndex % COLOR_SCHEMES.length];
  }
  
  // Fallback: hash the title to get consistent color
  const hash = title.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);
  return COLOR_SCHEMES[Math.abs(hash) % COLOR_SCHEMES.length];
};

const AppTile = ({
  icon,
  title,
  subtitle,
  url,
  className = "",
  testId = "app-tile",
  disabled = false,
  colorScheme, // Optional: specific color scheme index
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  // Memoize color scheme selection
  const colors = useMemo(() => 
    getColorScheme(title, colorScheme),
    [title, colorScheme]
  );

  // Memoize pathname calculation
  const pathname = useMemo(() => window.location.pathname, []);
  
  // Memoize icon source calculation
  const iconSrc = useMemo(() => 
    pathname === "/" ? icon : `${pathname}/${icon}`,
    [pathname, icon]
  );

  // Memoize click handler to prevent recreation
  const handleClick = useCallback(() => {
    if (!disabled && url) window.open(url, "_blank", "noopener,noreferrer");
  }, [disabled, url]);

  // Memoize keyboard handler
  const handleKeyDown = useCallback((e) => {
    if (!disabled && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      handleClick();
    }
  }, [disabled, handleClick]);

  // Memoize state setters with useCallback
  const handleMouseEnter = useCallback(() => !disabled && setIsHovered(true), [disabled]);
  const handleMouseLeave = useCallback(() => !disabled && setIsHovered(false), [disabled]);
  const handleMouseDown = useCallback(() => !disabled && setIsPressed(true), [disabled]);
  const handleMouseUp = useCallback(() => !disabled && setIsPressed(false), [disabled]);

  // Memoize icon type check
  const isImageIcon = useMemo(() => typeof icon === "string", [icon]);
  const IconComponent = !isImageIcon ? icon : null;

  // Memoize dynamic class strings
  const containerClasses = useMemo(() => 
    `relative rounded-2xl p-3 transition-all duration-300 transform flex items-center justify-between gap-3
    ${colors.bg} backdrop-blur-2xl ${colors.border} shadow-lg ${colors.shadow}
    ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
    ${isHovered ? `shadow-xl ${colors.shadowHover} scale-[1.03]` : ""}
    ${isPressed ? "scale-100" : ""}
    ${className}`,
    [disabled, isHovered, isPressed, className, colors]
  );

  const chevronClasses = useMemo(() =>
    `w-5 h-5 ${colors.chevron} transition-all duration-300 relative z-10 ${
      isHovered ? `translate-x-1 ${colors.chevronHover} scale-110` : ""
    }`,
    [isHovered, colors]
  );

  return (
    <div
      data-testid={testId}
      className={containerClasses}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={!disabled ? "button" : undefined}
      tabIndex={!disabled ? 0 : -1}
    >
      {/* Background layers - static elements */}
      <div className={`absolute inset-0 bg-gradient-to-br ${colors.gradientFrom} ${colors.gradientTo} rounded-2xl`} />
      <div className={`absolute inset-0 ${colors.backdrop} backdrop-blur-md rounded-2xl`} />
      <div
        className="absolute inset-0 opacity-[0.02] rounded-2xl"
        style={STATIC_BACKGROUND}
      />

      {/* Content */}
      <div className="flex items-center gap-3 flex-1 relative z-10">
        <div className={`p-2 rounded-2xl ${colors.iconBg} backdrop-blur-lg shadow-lg border ${colors.iconBorder}`}>
          {isImageIcon ? (
            <img
              src={iconSrc}
              alt={`${title} icon`}
              className="w-8 h-8 object-contain"
              loading="lazy"
            />
          ) : (
            IconComponent && (
              <IconComponent className={`w-8 h-8 ${colors.text}`} />
            )
          )}
        </div>

        <div>
          <h3 className={`${colors.text} font-bold text-base`}>{title}</h3>
          {subtitle && (
            <p className={`${colors.textMuted} text-xs leading-relaxed`}>
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {!disabled && (
        <ChevronRight className={chevronClasses} />
      )}

      {isHovered && !disabled && (
        <div className={`absolute inset-0 rounded-2xl border-2 ${colors.hoverBorder}`} />
      )}
    </div>
  );
};

// Export as memoized component to prevent unnecessary re-renders
export default React.memo(AppTile);